import { z } from "zod";

// Notifications (epic 07): a catalogue of trigger types, a person's settings
// per type and channel, and the in-app notices raised from them. S7.08 wires
// the triggers themselves (a finished scan, low credits, an invite, a failed
// payment); this story only gives them a seam to call.

export const NOTIFICATION_TYPES = [
  "scan-finished",
  "scan-failed",
  "scan-out-of-credits",
  "credits-low",
  "invite-received",
  "payment-failed",
  "access-request-received",
  "access-request-approved",
  "access-request-declined",
  "billing-usage-warning",
  "billing-recharge-failed",
  "manual-subscription-ended",
] as const;
export const notificationTypeSchema = z.enum(NOTIFICATION_TYPES);
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

/** Every channel a setting can choose. */
export const NOTIFICATION_CHANNELS = ["inApp", "email", "push"] as const;
export const notificationChannelSchema = z.enum(NOTIFICATION_CHANNELS);
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

/** What the settings screen shows for each type; not stored, just read by the browser. */
export type NotificationTypeDescription = {
  readonly type: NotificationType;
  readonly label: string;
  readonly description: string;
};

export const NOTIFICATION_TYPE_CATALOGUE: readonly NotificationTypeDescription[] = [
  {
    type: "scan-finished",
    label: "Scan finished",
    description: "An audience scan you started has finished.",
  },
  {
    type: "scan-failed",
    label: "Scan failed",
    description: "An audience scan you started could not finish.",
  },
  {
    type: "scan-out-of-credits",
    label: "Scan ran out of credits",
    description: "An audience scan you started ended because the organization ran out of credits.",
  },
  {
    type: "credits-low",
    label: "Credits low",
    description: "Your organization is running low on credits.",
  },
  {
    type: "invite-received",
    label: "Invite received",
    description: "Someone invited you to join an organization.",
  },
  {
    type: "payment-failed",
    label: "Payment failed",
    description: "A payment for your organization's plan failed.",
  },
  {
    type: "access-request-received",
    label: "Access request received",
    description: "Someone asked to join your organization.",
  },
  {
    type: "access-request-approved",
    label: "Access request approved",
    description: "Your request to join an organization was approved.",
  },
  {
    type: "access-request-declined",
    label: "Access request declined",
    description: "Your request to join an organization was declined.",
  },
  {
    type: "billing-usage-warning",
    label: "Usage warning",
    description: "Your organization is approaching or has reached its credit limit for the period.",
  },
  {
    type: "billing-recharge-failed",
    label: "Auto-recharge failed",
    description: "An automatic credit top-up for your organization could not be charged.",
  },
  {
    type: "manual-subscription-ended",
    label: "Manual plan ended",
    description: "A plan set by staff reached its end date and dropped to Free.",
  },
];

export const notificationChannelsSchema = z.object({
  inApp: z.boolean(),
  email: z.boolean(),
  push: z.boolean(),
});
export type NotificationChannels = z.infer<typeof notificationChannelsSchema>;

/** A person's setting for one notification type: on or off, and which channels fire while it is on. */
export const notificationSettingSchema = z.object({
  type: notificationTypeSchema,
  enabled: z.boolean(),
  channels: notificationChannelsSchema,
});
export type NotificationSetting = z.infer<typeof notificationSettingSchema>;

/** Every catalogued type, defaulted where the person never chose, in catalogue order. */
export const notificationSettingsSchema = z.array(notificationSettingSchema);
export type NotificationSettings = z.infer<typeof notificationSettingsSchema>;

export const setNotificationEnabledInputSchema = z.object({
  type: notificationTypeSchema,
  enabled: z.boolean(),
});
export type SetNotificationEnabledInput = z.infer<typeof setNotificationEnabledInputSchema>;

export const setNotificationChannelInputSchema = z.object({
  type: notificationTypeSchema,
  channel: notificationChannelSchema,
  enabled: z.boolean(),
});
export type SetNotificationChannelInput = z.infer<typeof setNotificationChannelInputSchema>;

// In-app notices (S7.02): one row per person per raised event, with an
// organization id kept for later scoping (never shown to anyone else yet).
export const NOTICE_TITLE_MAX = 160;
export const NOTICE_BODY_MAX = 500;

export const noticeSchema = z.object({
  id: z.string(),
  userId: z.string(),
  organizationId: z.string(),
  type: notificationTypeSchema,
  title: z.string().min(1).max(NOTICE_TITLE_MAX),
  body: z.string().max(NOTICE_BODY_MAX).nullable(),
  readAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type Notice = z.infer<typeof noticeSchema>;

export const noticesSchema = z.array(noticeSchema);

export const NOTICES_LIMIT_MAX = 100;
export const noticesForUserInputSchema = z.object({
  limit: z.number().int().min(1).max(NOTICES_LIMIT_MAX).default(30),
});
export type NoticesForUserInput = z.infer<typeof noticesForUserInputSchema>;

export const unreadNoticeCountSchema = z.object({ count: z.number().int().nonnegative() });
export type UnreadNoticeCount = z.infer<typeof unreadNoticeCountSchema>;

export const markNoticeReadInputSchema = z.object({ noticeId: z.string() });
export type MarkNoticeReadInput = z.infer<typeof markNoticeReadInputSchema>;

export const markAllNoticesReadOutputSchema = z.object({ updated: z.number().int().nonnegative() });
export type MarkAllNoticesReadOutput = z.infer<typeof markAllNoticesReadOutputSchema>;

export const emptyInputSchema = z.object({});

// Do not disturb (S7.05), bursts (S7.06) and digests (S7.07): a person's own
// preference, read beside their settings. A snooze pauses up to a week; quiet
// hours are a daily window in their own time zone (account's existing
// `preferences.timezone`, D076); an email digest trades every event for one
// roundup a day or a week. In-app notices always land; only the email
// channel is held while a snooze or quiet hours is active, and only the
// email channel ever digests (D079).

export const EMAIL_DIGEST_FREQUENCIES = ["immediate", "daily", "weekly"] as const;
export const emailDigestFrequencySchema = z.enum(EMAIL_DIGEST_FREQUENCIES);
export type EmailDigestFrequency = (typeof EMAIL_DIGEST_FREQUENCIES)[number];

export const SNOOZE_DAYS_MIN = 1;
export const SNOOZE_DAYS_MAX = 7;

/** A daily window, in the person's own time zone, local hours 0-23; `end` may be before `start` (wraps past midnight). */
export const quietHoursSchema = z.object({
  startHour: z.number().int().min(0).max(23),
  endHour: z.number().int().min(0).max(23),
});
export type QuietHours = z.infer<typeof quietHoursSchema>;

export const notificationPreferenceSchema = z.object({
  snoozeUntil: z.iso.datetime().nullable(),
  quietHours: quietHoursSchema.nullable(),
  emailDigest: emailDigestFrequencySchema,
  /** Marketing email (S6.09), on by default: product updates and tips. Account's email
   * preferences screen reads and writes these; notification owns the one copy. */
  productUpdates: z.boolean(),
  tips: z.boolean(),
});
export type NotificationPreference = z.infer<typeof notificationPreferenceSchema>;

export const setSnoozeInputSchema = z.object({
  days: z.number().int().min(SNOOZE_DAYS_MIN).max(SNOOZE_DAYS_MAX),
});
export type SetSnoozeInput = z.infer<typeof setSnoozeInputSchema>;

export const setQuietHoursInputSchema = z.object({
  enabled: z.boolean(),
  startHour: z.number().int().min(0).max(23).optional(),
  endHour: z.number().int().min(0).max(23).optional(),
});
export type SetQuietHoursInput = z.infer<typeof setQuietHoursInputSchema>;

export const setEmailDigestInputSchema = z.object({
  frequency: emailDigestFrequencySchema,
});
export type SetEmailDigestInput = z.infer<typeof setEmailDigestInputSchema>;

export const setProductUpdatesInputSchema = z.object({ enabled: z.boolean() });
export type SetProductUpdatesInput = z.infer<typeof setProductUpdatesInputSchema>;

export const setTipsInputSchema = z.object({ enabled: z.boolean() });
export type SetTipsInput = z.infer<typeof setTipsInputSchema>;

// A mailed unsubscribe link (RFC 8058): no caller, no session, just a signed
// token naming who and what. A GET only ever shows the confirmation screen;
// the RFC 8058 one-click POST (REST) or that screen's button (tRPC) spends it.
// email-delivery.feature holds the scenarios.

/** The platform screen a mailed link opens: confirms before anything changes. */
export const UNSUBSCRIBE_SCREEN_PATH = "/notifications/unsubscribe";
/** The `List-Unsubscribe` URL: a mail client's one-click POST lands here. */
export const UNSUBSCRIBE_ONE_CLICK_PATH = "/api/notifications/unsubscribe";

export const unsubscribeInputSchema = z.object({
  token: z
    .string()
    .optional()
    .transform((value) => value ?? ""),
});
export type UnsubscribeInput = z.infer<typeof unsubscribeInputSchema>;

export const unsubscribeOutcomeSchema = z.object({ ok: z.boolean() });
export type UnsubscribeOutcome = z.infer<typeof unsubscribeOutcomeSchema>;

/** Where a GET of the one-click URL hands a person over to: the confirmation screen, token kept. */
export const unsubscribeConfirmationSchema = z.object({ url: z.string() });
export type UnsubscribeConfirmation = z.infer<typeof unsubscribeConfirmationSchema>;
